import React, { useState, useEffect, useCallback, useRef } from 'react';
import { UserDetails as UserDetailsType, WorkspaceFilter, SubscriptionFilter } from '../types/billing.ts';
import api from '../services/api.ts';

interface UserDetailsProps {
  userId: string;
  onBack: () => void;
  onViewProject: (id: string) => void;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function subscriptionLabel(sub: SubscriptionFilter) {
  const start = fmtDate(sub.current_period_start);
  const end = fmtDate(sub.current_period_end);
  const statusNote = sub.status !== 'active' ? ` (${sub.status})` : '';
  return `${sub.billing_cycle} · ${start} – ${end}${statusNote}`;
}

const UserDetails: React.FC<UserDetailsProps> = ({ userId, onBack, onViewProject }) => {
  const [userDetails, setUserDetails] = useState<UserDetailsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [workspaces, setWorkspaces] = useState<WorkspaceFilter[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionFilter[]>([]);

  // null = "no filter" (all); a number = specific id
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);

  // Tracks whether the initial workspace has been decided (so we can gate the data fetch)
  const [workspaceReady, setWorkspaceReady] = useState(false);

  // ── 1. Load workspaces once; set default to first ───────────────────────────
  useEffect(() => {
    let cancelled = false;
    api.billing.getUserFilters(userId)
      .then((data) => {
        if (cancelled) return;
        setWorkspaces(data.workspaces);
        const defaultId = data.workspaces.length > 0 ? data.workspaces[0].id : null;
        setSelectedWorkspaceId(defaultId);
        setWorkspaceReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load workspaces:', err);
        setWorkspaceReady(true); // still allow the page to render
      });
    return () => { cancelled = true; };
  }, [userId]);

  // ── 2. Reload subscriptions whenever workspace changes ───────────────────────
  // workspaceReady gates this so it only runs after the initial workspace is chosen.
  useEffect(() => {
    if (!workspaceReady) return;
    let cancelled = false;
    api.billing.getUserFilters(userId, {
      workspace_id: selectedWorkspaceId ?? undefined,
    })
      .then((data) => {
        if (cancelled) return;
        setSubscriptions(data.subscriptions);
        // Reset subscription selection — old choice may not exist in new list
        setSelectedSubscriptionId(null);
      })
      .catch((err) => console.error('Failed to load subscriptions:', err));
    return () => { cancelled = true; };
  }, [userId, selectedWorkspaceId, workspaceReady]);

  // ── 3. Fetch user billing data when any filter changes ────────────────────────
  // We use a ref-based approach so we only fire when the filters themselves settle,
  // not on every intermediate render.
  const fetchUserDetails = useCallback(async (wsId: number | null, subId: number | null) => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.billing.getUser(userId, {
        workspace_id: wsId ?? undefined,
        subscription_id: subId ?? undefined,
      });
      setUserDetails(data);
    } catch (err) {
      console.error('Error loading user details:', err);
      setError('Failed to load user details. Please check if the API server is running.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Only fetch data once both filters are settled (workspace ready + subscription reset done)
  // We track selectedSubscriptionId via ref to avoid stale closures.
  const subIdRef = useRef(selectedSubscriptionId);
  useEffect(() => { subIdRef.current = selectedSubscriptionId; }, [selectedSubscriptionId]);

  useEffect(() => {
    if (!workspaceReady) return;
    fetchUserDetails(selectedWorkspaceId, selectedSubscriptionId);
  }, [fetchUserDetails, workspaceReady, selectedWorkspaceId, selectedSubscriptionId]);

  // ── Workspace change handler — resets subscription synchronously ─────────────
  const handleWorkspaceChange = useCallback((wsId: number | null) => {
    setSelectedWorkspaceId(wsId);
    setSelectedSubscriptionId(null); // reset before re-fetch so only one data fetch fires
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────────
  if (!workspaceReady || loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900 border border-red-700 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-lg font-medium text-red-400">Error Loading User Details</h3>
            <p className="text-red-300 mt-1">{error}</p>
            <button
              onClick={() => fetchUserDetails(selectedWorkspaceId, selectedSubscriptionId)}
              className="mt-3 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-400">User not found</h3>
        <p className="mt-1 text-sm text-gray-500">The requested user could not be found.</p>
        <button onClick={onBack} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
        <div className="mb-4 sm:mb-0">
          <button
            onClick={onBack}
            className="text-blue-400 hover:text-blue-300 mb-2 flex items-center transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Users
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{userDetails.user.name}</h1>
          <p className="text-gray-400">{userDetails.user.email}</p>
          <p className="text-sm text-gray-500">ID: {userDetails.user.user_id}</p>
          {userDetails.user.created_at && (
            <p className="text-sm text-gray-500">Joined: {fmtDate(userDetails.user.created_at)}</p>
          )}
        </div>
        <div className="text-left sm:text-right">
          <p className="text-sm text-gray-400">Total Cost</p>
          <p className="text-2xl sm:text-3xl font-bold text-white">${userDetails.total_cost.toFixed(2)}</p>
          <p className="text-sm text-green-400">${userDetails.total_user_cost.toFixed(2)} user cost</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 bg-gray-800 rounded-xl border border-gray-700 p-4">
        <div className="flex-1 min-w-0">
          <label className="block text-xs text-gray-400 mb-1">Workspace</label>
          <select
            value={selectedWorkspaceId ?? ''}
            onChange={(e) => handleWorkspaceChange(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Workspaces</option>
            {workspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name} ({ws.type})
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-0">
          <label className="block text-xs text-gray-400 mb-1">Subscription period</label>
          <select
            value={selectedSubscriptionId ?? ''}
            onChange={(e) => setSelectedSubscriptionId(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Subscriptions (Lifetime)</option>
            {subscriptions.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {subscriptionLabel(sub)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Credit Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-2">Credits Assigned</h3>
          <p className="text-2xl font-bold text-blue-400">{userDetails.credits_assigned.toFixed(2)}</p>
        </div>
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-2">Credits Consumed</h3>
          <p className="text-2xl font-bold text-red-400">{userDetails.total_credits_consumed.toFixed(2)}</p>
        </div>
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-2">Credits Remaining</h3>
          <p className="text-2xl font-bold text-green-400">
            {(userDetails.credits_assigned - userDetails.total_credits_consumed).toFixed(2)}
          </p>
        </div>
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-2">Utilization</h3>
          <p className="text-2xl font-bold text-yellow-400">
            {userDetails.credits_assigned > 0
              ? ((userDetails.total_credits_consumed / userDetails.credits_assigned) * 100).toFixed(1)
              : '0.0'}%
          </p>
        </div>
      </div>

      {/* Service Breakdown */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Service Cost Breakdown</h2>
        {Object.keys(userDetails.cost_breakdown).length === 0 ? (
          <p className="text-center text-gray-400 py-6">No usage data for the selected filters.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Object.entries(userDetails.cost_breakdown).map(([serviceName, service], index) => (
              <div key={serviceName} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-white capitalize">{service.service_name}</h3>
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                </div>
                <p className="text-lg font-medium text-white capitalize text-gray">{serviceName.split('-')[1]}</p>
                <br />
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Cost (USD)</span>
                    <span className="text-sm text-white font-medium">${service.cost.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Credits</span>
                    <span className="text-sm text-white font-medium">{service.credits.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Records</span>
                    <span className="text-sm text-white font-medium">{service.records}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Total Units</span>
                    <span className="text-sm text-white font-medium">{service.total_units.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Unit Type</span>
                    <span className="text-sm text-white font-medium">{service.unit_of_measure}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User Projects */}
      <div className="bg-gray-800 rounded-xl border border-gray-700">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg sm:text-xl font-semibold text-white">User Projects</h2>
        </div>
        <div className="p-4 sm:p-6">
          {userDetails.projects && userDetails.projects.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {userDetails.projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 cursor-pointer transition-colors duration-200"
                  onClick={() => onViewProject(project.id)}
                >
                  <h3 className="text-lg font-medium text-white mb-2 truncate" title={project.name}>
                    {project.name}
                  </h3>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">Cost</span>
                      <span className="text-sm text-white font-medium">${project.current_cost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">Trend</span>
                      <span className={`text-sm font-medium ${project.monthly_trend > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {project.monthly_trend > 0 ? '+' : ''}{project.monthly_trend}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">No projects found for the selected filters.</div>
          )}
        </div>
      </div>

      {/* User Information */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">User Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-400">Account Status</p>
            <span className={`inline-block px-2 py-1 rounded-full text-xs ${userDetails.user.is_active ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
              {userDetails.user.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-400">Monthly Trend</p>
            <span className={`font-medium ${userDetails.monthly_trend > 0 ? 'text-red-400' : 'text-green-400'}`}>
              {userDetails.monthly_trend > 0 ? '+' : ''}{userDetails.monthly_trend}%
            </span>
          </div>
          {userDetails.last_activity && (
            <div>
              <p className="text-sm text-gray-400">Last Activity</p>
              <p className="text-white">{new Date(userDetails.last_activity).toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
